
import { DecimalConfig } from "@/models/dossier-configs/decimal-config";
import { DossierConfigService } from "@/services/dossier-config-service";
import { ProductRow } from "@/utils/product-row";
import { durationToString } from "@/utils/duration";
import Vue from "vue";
import { SaleEditState } from "@/utils/sale-edit/sale-edit-state";
import { SaleRow } from "@/models/base";
import { ISelectItem } from "winfakt-vue-components";
import { printDuration, printHumanFloat } from "@/utils/pretty-print";
import {ProductSupplier} from "@/utils/product-supplier";
import { Highlight } from "@/utils/sale-edit/sale-edit-highlight";
import { Condition } from "@/utils/condition/condition";

export default Vue.extend({
	filters: {
		dToString (value:number) {
			return durationToString(value);
		}
	},
	props: {
		state: {
			type: Object as ()=>SaleEditState,
			required: true
		},
		rowIndex: {
			type: Number,
			required: true
		},
		showQty2: Boolean,
		showUnits: Boolean
	},
	computed: {
		display():string{
			if (this.state.hiddenRowsAreInvisble && this.row.SaleRowConfig.Hidden) {
				return "none";
			}
			return "";
		},
		hasImage():boolean{
			if(this.row.CoverImageOverrideFileID || this.row.Product?.CoverImageID){
				return true;
			}
			return false;
		},
		row():SaleRow{
			return this.state.sale.Rows[this.rowIndex];
		},
		displayedProductDescription():String{
			return this.state.getDisplayedRowProductDescription(this.row.ProductDescription);
		},
		isSelected():boolean {
			return this.state.selectedRow == this.row;
		},
		isMultiSelected():boolean {
			return this.state.rowSelection.isRowMultiSelected(this.row);
		},
		decimalSettings():DecimalConfig {
			return DossierConfigService.getDecimalSettings();
		},
		isBold(): boolean {
			return this.row.getRowConfig().Bold;
		},
		isHidden(): boolean {
			return this.row.getRowConfig().Hidden;
		},
		isSubTotal(): boolean {
			return this.row.getRowConfig().SubTotal;
		},
		unit(): number {
			return this.row.UnitPrice / (this.state.sale.CurrencyRate != 0 ? this.state.sale.CurrencyRate : 1);
		},
		netto(): number {
			//return this.row.NettoPrice;
			return this.row.Amount * ((this.unit)  * (1 - (this.row.Discount / 100.0)) + this.row.getVatInclusiveTaxesPerUnit()) ;
		},
		bruto(): number {
			return (this.row.BrutoPrice/this.state.sale.CurrencyRate) + (this.row.Amount * (this.row.LeeggoedTax + this.row.CustomVatExclusiveTax));
		},
		supplierCode(): string {
			let s = this.row.Product?.Suppliers.filter((s: ProductSupplier) => s.ContactID == this.state.sale.ContactID)[0];
			if(s){
				return s.SupplierCode;
			}
			return "";
		},
		options(): ISelectItem<Function>[] {
			let data:ISelectItem<Function>[] = [
				{text: "Verwijder lijn", value: this.remove},
				{text: this.$ct("insert-row").toString(), value: this.insertRowBefore},
				{
					value: this.bold,
					text: (this.row.getRowConfig().Bold ? this.$ct("make-regular").toString() : this.$ct("make-bold").toString())
				},
				{
					value: this.hide,
					text: (this.row.getRowConfig().Hidden ? this.$ct("row-show").toString() : this.$ct("row-hide").toString())
				},
				{
					value: this.moveUp,
					text: this.$ct("move-up").toString()
				},
				{
					value: this.moveDown,
					text: this.$ct("move-down").toString()
				},
				{
					value: this.editDeliveryDate,
					text: this.$ct("edit-delivery-date").toString()
				},
				{
					value: this.editExtraFields,
					text: this.$ct("edit-extra-fields").toString()
				},
				{
					value: this.showImageUpload,
					text: this.$ct("show-image-upload").toString()
				},
				{
					value: this.togglePageBreakBefore,
					text: this.row.SaleRowConfig.PageBreakBefore ? this.$ct("disable-page-break-before") : this.$ct("enable-page-break-before")
				}
			];
			return data;
		},
		isAutomatic():boolean {
			return this.row.CreatedByAutoSaleRowSettingID != 0;
		},
		justScanned():boolean {
			return this.state.justScannedRow == this.row;
		},
		justScannedInitial():boolean {
			return this.justScanned && this.state.justScannedRowWasScannedThisTick;
		},
		transitionDuration():string {
			if (this.justScanned) {
				return this.state.justScannedRowEffectTime + "ms";
			}
			return "0";
		},
		matchedHighlightRule():Highlight | null {
			if (!this.state.dossierConfig) return null;
			let allSettings = this.state.dossierConfig.SaleEditHighlightSettings;
			if (!allSettings) return null;
			for (let setting of allSettings) {
				if (Condition.checkConditions(setting.Conditions, this.row)){
					return setting;
				}
			}
			return null;
		},
		backgroundColorOverride():string | undefined {
			if (!this.matchedHighlightRule) return undefined;
			return this.matchedHighlightRule.Style.BackgroundColor;
		},
		colorOverride():string | undefined {
			if (!this.matchedHighlightRule) return undefined;
			return this.matchedHighlightRule.Style.Color;
		}
	},
	watch: {
		"row": {
			immediate: true,
			handler(){
				if (this.row.Sale) return;
				this.row.Sale = this.state.sale;
			}
		}
	},
	methods: {
		togglePageBreakBefore(){
			this.row.SaleRowConfig.PageBreakBefore = !this.row.SaleRowConfig.PageBreakBefore;
		},
		onEdit(event?: MouseEvent) {
			if (this.isAutomatic) return;
			if (event && (event.ctrlKey || event.metaKey)) {
				this.state.rowSelection.toggle(this.row);
				return;
			}
			if (event && event.shiftKey) {
				this.state.rowSelection.selectRange(this.row);
				return;
			}
			this.state.rowSelection.clear();
			if (this.row == this.state.selectedRow) {
				this.state.selectedRow = new SaleRow();
				this.state.selectedRow.Discount =this.state.sale.Contact?.Discount || 0;
				return;
			}
			this.state.selectedRow = this.row;
		},
		remove() {
			this.state.removeRow(this.row);
		},
		forceSelect(row: ProductRow) {
			if (!this.isSelected) {
				this.$emit("select", row);
			}
		},
		insertRowBefore() {
			let index = this.rowIndex;
			this.state.insertRowBefore(index);
		},
		bold() {
			this.row.getRowConfig().Bold = !this.row.getRowConfig().Bold;
		},
		hide() {
			this.row.getRowConfig().Hidden = !this.row.getRowConfig().Hidden;
			this.state.sale.calculateTotals();
		},
		async moveUp(){
			let row = new SaleRow(this.row.getJSON());
			await this.state.removeRow(this.row);
			this.state.sale.Rows = [row, ...this.state.sale.Rows];
			this.state.sale.Rows.map((r,i)=>{
				r.DisplayOrder = i;
			});
			this.state.selectedRow = new SaleRow();
			this.state.scrollToTop();
		},
		async moveDown(){
			let row = new SaleRow(this.row.getJSON());
			await this.state.removeRow(this.row);
			this.state.sale.Rows = [...this.state.sale.Rows, row];
			this.state.sale.Rows.map((r,i)=>{
				r.DisplayOrder = i;
			});
			this.state.selectedRow = new SaleRow();
			this.state.scrollToBottom();
		},
		editDeliveryDate(){
			this.$emit("edit-delivery-date",this.row);
		},
		humanFloat(val:number, decimals:number){
			return printHumanFloat(val, decimals);
		},
		humanDuration(val:number){
			return printDuration(val);
		},
		editExtraFields(){
			this.state.toEditRowExtraFields = this.row;
		},
		showImageUpload(){
			this.$emit("show-image-upload",this.row);
		}
	},
	i18nEx: {
		componentPrefix: "components.products-table"
	}
});
